import { QueryClient } from "@tanstack/react-query";

import type { ApiError } from "@/lib/api/client";

function isApiError(err: unknown): err is ApiError {
  return typeof err === "object" && err !== null && "status" in err;
}

/**
 * Shared QueryClient.
 *
 * Conventions:
 *  - 30s staleTime so navigating back to a screen doesn't refetch instantly.
 *  - Never retry 4xx (auth/validation/not-found are not transient); retry other
 *    errors up to twice. The client's middleware already handles 401 via token
 *    refresh, so a 401 reaching here means refresh failed — don't hammer it.
 *  - Mutations don't retry by default (non-idempotent writes).
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (isApiError(error) && error.status >= 400 && error.status < 500) {
            return false;
          }
          return failureCount < 2;
        },
      },
      mutations: {
        retry: false,
      },
    },
  });
}
